import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff } from "lucide-react";

interface QRScannerProps {
  onScan: (qrToken: string) => void;
  isActive: boolean;
}

/**
 * Extracts the invitation token from a scanned QR code value.
 * Handles both full URLs (https://clara.app/invite/TOKEN) and raw tokens.
 */
function extractTokenFromUrl(text: string): string {
  const match = text.match(/\/invite\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : text;
}

export function QRScanner({ onScan, isActive }: QRScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isRunningRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    if (!isActive) {
      // Clean up scanner when not active
      if (scannerRef.current && isRunningRef.current) {
        scannerRef.current
          .stop()
          .catch(() => {
            // Ignore errors when stopping
          })
          .finally(() => {
            scannerRef.current = null;
            isRunningRef.current = false;
          });
      }
      return;
    }

    let cancelled = false;

    const startScanner = async () => {
      setIsStarting(true);
      setError(null);

      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            const token = extractTokenFromUrl(decodedText);
            onScan(token);
          },
          () => {
            // Ignore "QR code not found" errors - they happen constantly
          },
        );

        // Only mark as running if we weren't cancelled during start
        if (!cancelled) {
          isRunningRef.current = true;
        } else {
          // Component unmounted during start, clean up immediately
          scanner.stop().catch(() => {});
        }
      } catch (err) {
        if (cancelled) return;

        const message =
          err instanceof Error ? err.message : "No se pudo iniciar la cámara";

        if (message.includes("Permission")) {
          setError(
            "Permiso de cámara denegado. Habilitá el acceso a la cámara para escanear códigos QR.",
          );
        } else if (message.includes("NotFoundError")) {
          setError(
            "No se encontró una cámara. Asegurate de que tu dispositivo tenga cámara.",
          );
        } else {
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsStarting(false);
        }
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      if (scannerRef.current && isRunningRef.current) {
        scannerRef.current.stop().catch(() => {
          // Ignore cleanup errors
        });
        scannerRef.current = null;
        isRunningRef.current = false;
      }
    };
  }, [isActive, onScan]);

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <CameraOff className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button
          variant="outline"
          onClick={() => {
            setError(null);
            // Trigger re-mount by toggling isActive
            window.location.reload();
          }}
        >
          <Camera className="mr-2 h-4 w-4" />
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        id="qr-reader"
        className="overflow-hidden rounded-xl bg-black"
        style={{ minHeight: "300px" }}
      />
      {isStarting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
          <div className="text-white text-sm">Iniciando cámara...</div>
        </div>
      )}
    </div>
  );
}
